/**
 * @file ClauseTrackingPlaceholderView.tsx
 * @description Placeholder body for the drawer / page "Live Tracking"
 *   tab (SE-4347 AC-6). Matches `proto-drawer-live-tracking.png`: a
 *   top banner noting the non-real data, then three sections —
 *   Appearances, Goals, and Financial Triggers — each with XX/XX
 *   progress bars and helper strings of the form "Triggers €XXX
 *   bonus at XX …". Wired up for real data in a later epic.
 * @module features/contracts/components/ClauseTrackingPlaceholder/ClauseTrackingPlaceholderView
 */

import React, { memo, type ReactNode } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Progress, ProgressFilledTrack } from "@/components/atoms/Progress";
import { Text } from "@/components/atoms/Text";

/**
 * Static meter row. The three "performance" sections (Appearances and
 * Goals) render a list of these with current / target counters.
 */
interface PerformanceMeter {
  /** Display label (e.g. "League Appearances"). */
  readonly label: string;
  /** Kebab-case key used in the testID for the meter row. */
  readonly key: string;
  /** Helper string under the bar (e.g. "Triggers €XXX bonus at XX appearances"). */
  readonly helper: string;
}

/**
 * Static "Financial Triggers" row. Distinct from performance meters
 * because these don't have XX/XX counters — they're clause-driven
 * percentages or flat bonuses with helper text only.
 */
interface FinancialTrigger {
  /** Display label (e.g. "Sell-on Clause: XX%"). */
  readonly label: string;
  /** Kebab-case key used in the testID for the trigger row. */
  readonly key: string;
  /** Supporting detail ("XX% of future transfer fee payable to previous club"). */
  readonly helper: string;
}

const APPEARANCE_METERS: readonly PerformanceMeter[] = [
  {
    label: "League Appearances: XX / XX",
    key: "league-appearances",
    helper: "Triggers €XXX bonus at XX appearances",
  },
  {
    label: "Total Appearances: XX / XX",
    key: "total-appearances",
    helper: "Triggers €XXX bonus at XX appearances",
  },
];

const GOAL_METERS: readonly PerformanceMeter[] = [
  {
    label: "Goals: XX / XX",
    key: "goals",
    helper: "Triggers €XXX bonus at XX goals",
  },
];

const FINANCIAL_TRIGGERS: readonly FinancialTrigger[] = [
  {
    label: "Sell-on Clause: XX%",
    key: "sell-on-clause",
    helper: "XX% of future transfer fee payable to previous club",
  },
  {
    label: "Loyalty Bonus",
    key: "loyalty-bonus",
    helper: "Triggers €XXX after XX years of service",
  },
];

/**
 * Render helper for one performance meter row (label + progress +
 * helper copy). Declared as a plain function returning JSX — NOT a
 * component — so it doesn't trip the one-component-per-file rule and
 * stays a tiny render utility local to this file.
 * @param meter - Static meter descriptor
 * @returns The rendered row
 */
function renderPerformanceMeter(meter: PerformanceMeter): ReactNode {
  return (
    <Box
      key={meter.key}
      testID={`clause-tracking-meter-${meter.key}`}
      className="gap-1"
    >
      <Stack className="gap-1">
        <Stack direction="horizontal" className="items-center justify-between">
          <Text variant="body" className="text-content-secondary">
            {meter.label}
          </Text>
          <Text variant="body-strong" className="text-content-muted">
            —%
          </Text>
        </Stack>
        <Progress value={0} size="sm" orientation="horizontal">
          <ProgressFilledTrack />
        </Progress>
        <Text variant="caption" className="text-content-muted">
          {meter.helper}
        </Text>
      </Stack>
    </Box>
  );
}

/**
 * Render helper for one financial-trigger row (label + helper only —
 * no progress bar; the prototype treats these as clause-text entries).
 * @param trigger - Static trigger descriptor
 * @returns The rendered row
 */
function renderFinancialTrigger(trigger: FinancialTrigger): ReactNode {
  return (
    <Box
      key={trigger.key}
      testID={`clause-tracking-trigger-${trigger.key}`}
      className="gap-1"
    >
      <Stack className="gap-1">
        <Text variant="body" className="text-content-secondary">
          {trigger.label}
        </Text>
        <Text variant="caption" className="text-content-muted">
          {trigger.helper}
        </Text>
      </Stack>
    </Box>
  );
}

/**
 * Renders the Live Tracking placeholder content. Entirely static — the
 * wiring to live performance data lives in a later epic.
 * @returns The rendered placeholder
 */
const ClauseTrackingPlaceholderView = () => (
  <Box testID="clause-tracking-placeholder">
    <Box className="rounded-sm border border-surface-muted bg-surface-base p-4">
      <Stack className="gap-5">
        <Box className="rounded-none border border-surface-muted bg-surface-subtle px-3 py-2">
          <Text variant="body" className="text-content-secondary">
            This is an example component not reflecting real data. Live tracking
            will be available soon.
          </Text>
        </Box>

        <Box testID="clause-tracking-section-appearances">
          <Stack className="gap-3">
            <Text variant="title-sm" className="text-content-primary">
              Appearances
            </Text>
            {APPEARANCE_METERS.map(renderPerformanceMeter)}
          </Stack>
        </Box>

        <Box testID="clause-tracking-section-goals">
          <Stack className="gap-3">
            <Text variant="title-sm" className="text-content-primary">
              Goals
            </Text>
            {GOAL_METERS.map(renderPerformanceMeter)}
          </Stack>
        </Box>

        <Box testID="clause-tracking-section-financial-triggers">
          <Stack className="gap-3">
            <Text variant="title-sm" className="text-content-primary">
              Financial Triggers
            </Text>
            {FINANCIAL_TRIGGERS.map(renderFinancialTrigger)}
          </Stack>
        </Box>
      </Stack>
    </Box>
  </Box>
);

ClauseTrackingPlaceholderView.displayName = "ClauseTrackingPlaceholderView";

export default memo(ClauseTrackingPlaceholderView);
